import * as React from "react"
import { type LucideIcon } from "lucide-react"
import { Link, usePage } from "@inertiajs/react"

import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

function matchesPattern(currentUrl: string, pattern: string): boolean {
  if (pattern.includes('?')) {
    const [basePath, queryString] = pattern.split('?')
    if (!currentUrl.startsWith(basePath)) return false
    const patternParams = new URLSearchParams(queryString)
    const urlParams = new URLSearchParams(currentUrl.split('?')[1] || '')
    return Array.from(patternParams.entries()).every(([key, value]) => urlParams.get(key) === value)
  }
  return currentUrl.startsWith(pattern)
}

export function NavGroup({
  title,
  items,
  totalBadge,
  collapsedIcon,
  collapsedUrl,
  ...props
}: {
  title: string
  items: {
    title: string
    url: string
    icon?: LucideIcon
    activePattern?: string
    badge?: number
  }[]
  totalBadge?: number
  collapsedIcon?: LucideIcon
  collapsedUrl?: string
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { url } = usePage()
  const CollapsedIcon = collapsedIcon

  return (
    <SidebarGroup {...props}>
      <SidebarMenu>
        {/* Collapsed mode: show single icon if collapsedIcon provided, otherwise show all items */}
        <div className="hidden group-data-[collapsible=icon]:block">
          {CollapsedIcon && collapsedUrl ? (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={title} isActive={items.some(item => matchesPattern(url, item.activePattern || item.url))}>
                <Link href={collapsedUrl} className="relative">
                  <CollapsedIcon />
                  <span>{title}</span>
                  {!!totalBadge && totalBadge > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-semibold text-white shadow-sm">
                      {totalBadge > 99 ? '99+' : totalBadge}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            items.map((item) => {
              const pattern = item.activePattern || item.url
              const isActive = matchesPattern(url, pattern)

              return (
                <SidebarMenuItem key={`icon-${item.title}`}>
                  <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
                    <Link href={item.url} className="relative">
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      {!!item.badge && item.badge > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-semibold text-white shadow-sm">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })
          )}
        </div>

        {/* Expanded mode: collapsible group with label header */}
        <Collapsible defaultOpen className="group/collapsible group-data-[collapsible=icon]:hidden">
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <button className="flex w-full items-center justify-between px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-sidebar-foreground/50 hover:text-sidebar-foreground/70 transition-colors cursor-pointer">
                <span>{title}</span>
                {!!totalBadge && totalBadge > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF3B30] px-1.5 text-[10px] font-semibold text-white shadow-sm">
                    {totalBadge > 99 ? '99+' : totalBadge}
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {items.map((item) => {
                  const pattern = item.activePattern || item.url
                  const isActive = matchesPattern(url, pattern)

                  return (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive} className="relative">
                        <Link href={item.url}>
                          {item.icon && <item.icon className="size-4" />}
                          <span>{item.title}</span>
                          {!!item.badge && item.badge > 0 && (
                            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF3B30] px-1.5 text-[10px] font-semibold text-white shadow-sm">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )
                })}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  )
}
